// src/pages/entit/histology.js
import React, { useMemo, useState } from "react";
import { Container, Row, Col, Form, Table } from "react-bootstrap";
import ReactECharts from "echarts-for-react";
import donutData from "../../assets/data/donut.json";
import "../../App.css";

// ⭐ added
import { DATENSTAND } from "../../config/constants";
import { exportToExcel } from "../../utils/exportExcel";
import { HISTOLOGY_COLORS } from "../../config/colors";

// --- formatters ---
const nfInt = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 0,
});

const nfPct1 = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

const fmtInt = (v) =>
  v == null
	    ? "–"
    : nfInt.format(v);

const fmtPct = (v) =>
  v == null
	    ? "–"
    : nfPct1.format(v);

const uniqueSorted = (arr) =>
  [...new Set(arr)].sort(
    (a, b) =>
      String(a).localeCompare(
        String(b),
        "de"
      )
  );

// ------------------------------------------------------
// STABLE HISTOLOGY COLORS PER ENTITY
//
// For each entity:
//
// 1. Start with the earliest available year.
// 2. Order groups that first appear in that year
//    by frequency.
// 3. Assign colors from HISTOLOGY_COLORS.
// 4. Existing groups keep their color in all
//    later years.
// 5. New groups appearing later get the next
//    palette color.
// 6. The same palette starts again for each entity.
// ------------------------------------------------------

const buildHistologyColorMap = (
  entityRows
) => {
  const colorMap = {};

  const years = [
    ...new Set(
      entityRows
        .map(
          (d) =>
            Number(
              d.diayear
            )
        )
        .filter(
          Number.isFinite
        )
    ),
  ].sort(
    (a, b) =>
      a - b
  );

  let nextColorIndex = 0;

  years.forEach(
    (currentYear) => {
      const yearRows =
        entityRows.filter(
          (d) =>
            Number(
              d.diayear
            ) ===
            currentYear
        );

      const frequencyByGroup =
        new Map();

      /*
       * Calculate total frequency for each
       * histology group in this year.
       *
       * Men and women are added together here
       * so that the initial color assignment
       * represents the whole entity.
       */
      yearRows.forEach(
        (row) => {
          const group =
            row["Eingruppierung"] ||
            "Unbekannt";

          const n =
            Number(
              row.Anzahl || 0
            );

          frequencyByGroup.set(
            group,
            (
              frequencyByGroup.get(
                group
              ) ?? 0
            ) + n
          );
        }
      );

      /*
       * Only groups that do not yet have
       * a color are considered.
       *
       * New groups within the same year
       * are ordered by frequency.
       */
      const newGroups = [
        ...frequencyByGroup.entries(),
      ]
        .filter(
          ([group]) =>
            colorMap[
              group
            ] == null
        )
        .sort(
          (
            [groupA, freqA],
            [groupB, freqB]
          ) => {
            if (
              freqB !==
              freqA
            ) {
              return (
                freqB -
                freqA
              );
            }

            /*
             * Stable alphabetical fallback
             * when frequencies are identical.
             */
            return String(
              groupA
            ).localeCompare(
              String(
                groupB
              ),
              "de"
            );
          }
        );

      /*
       * Give every new group the next
       * palette color.
       *
       * Once assigned, the colorMap entry
       * never changes.
       */
      newGroups.forEach(
        ([group]) => {
          colorMap[
            group
          ] =
            HISTOLOGY_COLORS[
              nextColorIndex %
                HISTOLOGY_COLORS.length
            ];

          nextColorIndex += 1;
        }
      );
    }
  );

  return colorMap;
};

const KrebsartenVerteilung = () => {
  const years = useMemo(
    () =>
      uniqueSorted(
        donutData.map(
          (d) =>
            d.diayear
        )
      ),
    []
  );

  const entits = useMemo(
    () =>
      uniqueSorted(
        donutData.map(
          (d) =>
            d.entit
        )
      ),
    []
  );

  const wrapLabel = (
    name,
    maxLen = 22
  ) => {
    if (!name) {
      return "";
    }

    const words =
      String(
        name
      ).split(" ");

    const lines = [];

    let current = "";

    words.forEach(
      (w) => {
        const test =
          (
            current +
            " " +
            w
          ).trim();

        if (
          test.length >
          maxLen
        ) {
          if (current) {
            lines.push(
              current.trim()
            );
          }

          current = w;
        } else {
          current = test;
        }
      }
    );

    if (current) {
      lines.push(
        current.trim()
      );
    }

    return lines.join(
      "\n"
    );
  };

  const [
    year,
    setYear,
  ] = useState(
    String(
      years[
        years.length - 1
      ] ?? ""
    )
  );

  const [
    entit,
    setEntit,
  ] = useState(
    entits[0] ?? ""
  );

  const filtered =
    useMemo(
      () =>
        donutData.filter(
          (d) =>
            String(
              d.diayear
            ) ===
              String(
                year
              ) &&
            d.entit ===
              entit
        ),
      [
        year,
        entit,
      ]
    );

  const maleRows =
    useMemo(
      () =>
        filtered.filter(
          (d) =>
            d.SEX ===
            "M"
        ),
      [filtered]
    );

  const femaleRows =
    useMemo(
      () =>
        filtered.filter(
          (d) =>
            d.SEX ===
            "W"
        ),
      [filtered]
    );

  // 🔹 aggregate by Eingruppierung
  // for charts, but also carry histogruppe2
  // as label
  const aggregateByGroup = (
    rows
  ) => {
    const map =
      new Map();

    let total = 0;

    rows.forEach(
      (r) => {
        const key =
          r["Eingruppierung"] ||
          "Unbekannt";

        const label =
          r["histogruppe2"] ||
          key;

        const n =
          Number(
            r.Anzahl || 0
          );

        total += n;

        if (
          !map.has(
            key
          )
        ) {
          map.set(
            key,
            {
              n: 0,
              label,
            }
          );
        }

        map.get(
          key
        ).n += n;
      }
    );

    const entries = [
      ...map.entries(),
    ]
      .map(
        ([
          name,
          info,
        ]) => ({
          // Eingruppierung for charts
          name,

          // histogruppe2 for table
          label:
            info.label,

          n:
            info.n,

          pct:
            total > 0
              ? (
                  info.n /
                  total
                ) *
                100
              : null,
        })
      )
      .sort(
        (a, b) =>
          a.name.localeCompare(
            b.name,
            "de"
          )
      );

    return {
      entries,
      total,
    };
  };

  const {
    entries:
      menGroups,
    total:
      totalM,
  } = useMemo(
    () =>
      aggregateByGroup(
        maleRows
      ),
    [maleRows]
  );

  const {
    entries:
      womenGroups,
    total:
      totalW,
  } = useMemo(
    () =>
      aggregateByGroup(
        femaleRows
      ),
    [femaleRows]
  );

  const totalAll =
    totalM +
    totalW;

  // ------------------------------------------------------
  // ALL DATA FOR CURRENT ENTITY
  //
  // Important:
  // This contains every available year for the
  // selected entity.
  //
  // Therefore changing only the year does not
  // rebuild the color meaning.
  // ------------------------------------------------------

  const entityRows =
    useMemo(
      () =>
        donutData.filter(
          (d) =>
            d.entit ===
            entit
        ),
      [entit]
    );

  // ------------------------------------------------------
  // STABLE COLOR MAP
  // ------------------------------------------------------

  const COLOR =
    useMemo(
      () =>
        buildHistologyColorMap(
          entityRows
        ),
      [entityRows]
    );

  // ⭐ add white borders
  const menPie =
    useMemo(
      () =>
        menGroups.map(
          (g) => ({
            name:
              g.name,

            value:
              g.pct ??
              0,

            n:
              g.n,

            itemStyle: {
              color:
                COLOR[
                  g.name
                ],

              borderWidth:
                1,

              borderColor:
                "#fff",
            },
          })
        ),
      [
        menGroups,
        COLOR,
      ]
    );

  const womenPie =
    useMemo(
      () =>
        womenGroups.map(
          (g) => ({
            name:
              g.name,

            value:
              g.pct ??
              0,

            n:
              g.n,

            itemStyle: {
              color:
                COLOR[
                  g.name
                ],

              borderWidth:
                1,

              borderColor:
                "#fff",
            },
          })
        ),
      [
        womenGroups,
        COLOR,
      ]
    );

  const basePieOption = {
    tooltip: {
      trigger:
        "item",

      backgroundColor:
        "#004c3f",

      borderWidth:
        0,

      textStyle: {
        color:
          "#fff",

        fontSize:
          12,
      },

      confine:
        true,

      formatter:
        (p) =>
          `<div style="text-align:left;line-height:1.3">
            <div style="font-weight:600;margin-bottom:4px">${p.name}</div>
            <div>Anzahl: ${fmtInt(
              p.data?.n
            )}</div>
            <div>Anteil: ${fmtPct(
              p.value
            )}%</div>
          </div>`,
    },

    legend: {
      show:
        false,
    },

    series: [],
  };

  const menOption = {
    ...basePieOption,

    series: [
      {
        type:
          "pie",

        radius: [
          "40%",
          "70%",
        ],

        center: [
          "50%",
          "50%",
        ],

        minShowLabelAngle:
          2,

        label: {
          show:
            true,

          formatter:
            (p) => {
              const wrapped =
                wrapLabel(
                  p.name
                );

              return `${wrapped}\n${fmtPct(
                p.value
              )}%`;
            },

          fontSize:
            12,
        },

        labelLine: {
          show:
            true,

          length:
            12,

          length2:
            8,
        },

        data:
          menPie,
      },
    ],
  };

  const womenOption = {
    ...basePieOption,

    series: [
      {
        type:
          "pie",

        radius: [
          "40%",
          "70%",
        ],

        center: [
          "50%",
          "50%",
        ],

        minShowLabelAngle:
          2,

        label: {
          show:
            true,

          formatter:
            (p) => {
              const wrapped =
                wrapLabel(
                  p.name
                );

              return `${wrapped}\n${fmtPct(
                p.value
              )}%`;
            },

          fontSize:
            12,
        },

        labelLine: {
          show:
            true,

          length:
            12,

          length2:
            8,
        },

        data:
          womenPie,
      },
    ],
  };

  // 🔹 table now uses histogruppe2
  // stored in g.label
  const tableRows =
    useMemo(() => {
      const map =
        new Map();

      const add = (
        g,
        sex
      ) => {
        if (
          !map.has(
            g.name
          )
        ) {
          map.set(
            g.name,
            {
              group:
                g.label,

              n_m:
                0,

              n_w:
                0,

              pct_m:
                null,

              pct_w:
                null,
            }
          );
        }

        const rec =
          map.get(
            g.name
          );

        if (
          sex ===
          "M"
        ) {
          rec.n_m +=
            g.n;

          rec.pct_m =
            totalM > 0
              ? (
                  rec.n_m /
                  totalM
                ) *
                100
              : null;
        } else {
          rec.n_w +=
            g.n;

          rec.pct_w =
            totalW > 0
              ? (
                  rec.n_w /
                  totalW
                ) *
                100
              : null;
        }
      };

      menGroups.forEach(
        (g) =>
          add(
            g,
            "M"
          )
      );

      womenGroups.forEach(
        (g) =>
          add(
            g,
            "W"
          )
      );

      const arr = [
        ...map.values(),
      ].map(
        (r) => {
          const n_g =
            r.n_m +
            r.n_w;

          const pct_g =
            totalAll > 0
              ? (
                  n_g /
                  totalAll
                ) *
                100
              : null;

          return {
            ...r,
            n_g,
            pct_g,
          };
        }
      );

      const SPECIAL_BOTTOM =
        "Neoplasien, sonstige oder ohne nähere Angabe";

      arr.sort(
        (a, b) => {
          if (
            a.group ===
            SPECIAL_BOTTOM
          ) {
            return 1;
          }

          if (
            b.group ===
            SPECIAL_BOTTOM
          ) {
            return -1;
          }

          return a.group.localeCompare(
            b.group,
            "de"
          );
        }
      );

      return arr;
    }, [
      menGroups,
      womenGroups,
      totalM,
      totalW,
      totalAll,
    ]);

  const footer =
    useMemo(
      () => ({
        n_g:
          totalAll,

        n_m:
          totalM,

        n_w:
          totalW,

        pct_g:
          100,

        pct_m:
          totalM > 0
            ? 100
            : null,

        pct_w:
          totalW > 0
            ? 100
            : null,
      }),
      [
        totalAll,
        totalM,
        totalW,
      ]
    );

  const Legend = ({
    cats,
  }) => {
    if (
      !cats?.length
    ) {
      return null;
    }

    return (
      <div
        style={{
          fontSize:
            12,

          margin:
            "6px 0 10px",
        }}
      >
        {cats.map(
          (c) => (
            <span
              key={
                c
              }
              style={{
                marginRight:
                  16,
              }}
            >
              <span
                style={{
                  display:
                    "inline-block",

                  width:
                    12,

                  height:
                    12,

                  borderRadius:
                    3,

                  backgroundColor:
                    COLOR[
                      c
                    ],

                  marginRight:
                    6,

                  verticalAlign:
                    "middle",
                }}
              />

              {c}
            </span>
          )
        )}
      </div>
    );
  };

  const menLegendCats =
    useMemo(
      () =>
        menGroups.map(
          (g) =>
            g.name
        ),
      [menGroups]
    );

  const womenLegendCats =
    useMemo(
      () =>
        womenGroups.map(
          (g) =>
            g.name
        ),
      [womenGroups]
    );

  // ⭐ EXPORT HANDLER
  const handleExport = () => {
    const excelRows =
      tableRows.map(
        (r) => ({
          Eingruppierung:
            r.group,

          Gesamt:
            fmtInt(
              r.n_g
            ),

          Männer:
            fmtInt(
              r.n_m
            ),

          Frauen:
            fmtInt(
              r.n_w
            ),

          "Gesamt (%)":
            fmtPct(
              r.pct_g
            ),

          "Männer (%)":
            fmtPct(
              r.pct_m
            ),

          "Frauen (%)":
            fmtPct(
              r.pct_w
            ),
        })
      );

    exportToExcel({
      filename:
        "histologie",

      sheetName:
        "Histologie",

      data:
        excelRows,

      metadata: [
        [
          "Histologie",
        ],

        [
          "Krebsregister Sachsen-Anhalt https://www.kr-lsa.de",
        ],

        [
          `Datenstand: ${DATENSTAND}`,
        ],

        [
          `Export am: ${new Date().toLocaleString(
            "de-DE"
          )}`,
        ],
      ],
    });
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">
        Histologie
      </h2>

      <div className="section-title-underline"></div>

      <div
        className="aktuelles-text"
        style={{
          marginTop:
            "0.5rem",
        }}
      >
        <p>
          Die Beschreibung der Morphologie erfolgt nach der International
          Classification of Diseases for Oncology (ICD-O-3). Sie
          charakterisiert den Zelltyp der Tumorerkrankung und gibt eine
          Aussage über die Dignität.
        </p>

        <p>
          Auf dieser Seite sehen Sie die Häufigkeit der Verteilung der
          Histologie für die 15 häufigsten Entitäten, die Sie über das
          Auswahlmenü für verschiedene Diagnosejahre aufrufen können.
        </p>
      </div>

      {/* ⭐ EXPORT BUTTON */}

      <button
        onClick={
          handleExport
        }
        className="top-btn mb-3"
      >
        📥 Daten als Excel exportieren
      </button>

      {/* Dropdowns */}

      <div
        className="d-flex align-items-center gap-3 mb-4"
        style={{
          maxWidth:
            700,
        }}
      >
        <Form.Group
          controlId="entitSelect"
          className="flex-fill"
        >
          <Form.Label>
            Krebs-Auswahl:
          </Form.Label>

          <Form.Select
            value={
              entit
            }
            onChange={(
              e
            ) =>
              setEntit(
                e.target.value
              )
            }
          >
            {entits.map(
              (e) => (
                <option
                  key={
                    e
                  }
                  value={
                    e
                  }
                >
                  {e}
                </option>
              )
            )}
          </Form.Select>
        </Form.Group>

        <Form.Group
          controlId="yearSelect"
          className="flex-fill"
        >
          <Form.Label>
            Jahr:
          </Form.Label>

          <Form.Select
            value={
              year
            }
            onChange={(
              e
            ) =>
              setYear(
                e.target.value
              )
            }
          >
            {years.map(
              (y) => (
                <option
                  key={
                    y
                  }
                  value={
                    y
                  }
                >
                  {y}
                </option>
              )
            )}
          </Form.Select>
        </Form.Group>
      </div>

      <Row className="mb-4">
        <Col md={6}>
          <h5 className="mb-2">
            Männer
          </h5>

          <Legend
            cats={
              menLegendCats
            }
          />

          <ReactECharts
            option={
              menOption
            }
            style={{
              width:
                "100%",

              height:
                420,
            }}
          />
        </Col>

        <Col md={6}>
          <h5 className="mb-2">
            Frauen
          </h5>

          <Legend
            cats={
              womenLegendCats
            }
          />

          <ReactECharts
            option={
              womenOption
            }
            style={{
              width:
                "100%",

              height:
                420,
            }}
          />
        </Col>
      </Row>

      <Table
        responsive
        className="zeitverlauf-table text-center align-middle"
        style={{
          maxWidth:
            "1000px",
        }}
      >
        <thead>
          <tr>
            <th className="text-start">
              Eingruppierung
            </th>

            <th colSpan={3}>
              Anzahl
            </th>

            <th colSpan={3}>
              Anteil in %
            </th>
          </tr>

          <tr>
            <th className="text-start"></th>

            <th>
              Gesamt
            </th>

            <th>
              Männer
            </th>

            <th>
              Frauen
            </th>

            <th>
              Gesamt
            </th>

            <th>
              Männer
            </th>

            <th>
              Frauen
            </th>
          </tr>
        </thead>

        <tbody>
          {tableRows.map(
            (r) => (
              <tr
                key={`grp-${r.group}`}
              >
                <td className="text-start">
                  {r.group}
                </td>

                <td>
                  {fmtInt(
                    r.n_g
                  )}
                </td>

                <td>
                  {fmtInt(
                    r.n_m
                  )}
                </td>

                <td>
                  {fmtInt(
                    r.n_w
                  )}
                </td>

                <td>
                  {fmtPct(
                    r.pct_g
                  )}
                </td>

                <td>
                  {fmtPct(
                    r.pct_m
                  )}
                </td>

                <td>
                  {fmtPct(
                    r.pct_w
                  )}
                </td>
              </tr>
            )
          )}
        </tbody>

        <tfoot>
          <tr className="table-secondary">
            <td className="text-start fw-bold">
              Gesamt
            </td>

            <td className="fw-bold">
              {fmtInt(
                footer.n_g
              )}
            </td>

            <td className="fw-bold">
              {fmtInt(
                footer.n_m
              )}
            </td>

            <td className="fw-bold">
              {fmtInt(
                footer.n_w
              )}
            </td>

            <td className="fw-bold">
              {fmtPct(
                footer.pct_g
              )}
            </td>

            <td className="fw-bold">
              {fmtPct(
                footer.pct_m
              )}
            </td>

            <td className="fw-bold">
              {fmtPct(
                footer.pct_w
              )}
            </td>
          </tr>
        </tfoot>
      </Table>
    </Container>
  );
};

export default KrebsartenVerteilung;
